import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Upload, message } from 'antd';
import { CloudUploadOutlined, FilePdfOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Text, Flex } from 'atoms';

// Styled Components
const ModalContent = styled(Box)`
	padding: 8px 0;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	margin-bottom: 8px;
`;

const ModalDescription = styled(Text)`
	font-size: 14px;
	color: #666;
	margin-bottom: 24px;
`;

const UploadArea = styled(Box)`
	border: 2px dashed #d9d9d9;
	border-radius: 8px;
	padding: 40px 20px;
	text-align: center;
	background: #fafafa;
	margin-bottom: 16px;
`;

const UploadText = styled(Text)`
	font-size: 13px;
	color: #999;
	margin-bottom: 16px;
	display: block;
`;

const UploadButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 24px;
	background: #fff;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	font-size: 14px;
	color: #000;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		border-color: #18405d;
		color: #18405d;
	}

	svg {
		font-size: 16px;
	}
`;

const TemplateLink = styled.a`
	color: #1890ff;
	text-decoration: none;
	font-size: 14px;

	&:hover {
		text-decoration: underline;
	}
`;

const FileItem = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	margin-bottom: 16px;
	background: #fff;
`;

const FileInfo = styled(Flex)`
	align-items: center;
	gap: 12px;
`;

const FileName = styled(Text)`
	font-size: 14px;
	color: #000;
`;

const DeleteButton = styled.button`
	background: none;
	border: none;
	color: #ff4d4f;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	font-size: 18px;
	transition: opacity 0.3s;

	&:hover {
		opacity: 0.7;
	}
`;

const ModalFooter = styled(Flex)`
	justify-content: space-between;
	gap: 16px;
	margin-top: 24px;
`;

const CancelButton = styled.button`
	flex: 1;
	padding: 10px 24px;
	background: #fff;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	font-size: 15px;
	font-weight: 500;
	color: #000;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		border-color: #18405d;
		color: #18405d;
	}
`;

const SubmitButton = styled.button`
	flex: 1;
	padding: 10px 24px;
	background: #18405d;
	border: none;
	border-radius: 4px;
	font-size: 15px;
	font-weight: 500;
	color: #fff;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		background: #1a4a6e;
	}

	&:disabled {
		background: #d9d9d9;
		cursor: not-allowed;
	}
`;

export default function BulkUploadModal({
	visible,
	onClose,
	onSubmit,
	onFileUpload,
	title = 'Bulk Upload',
	description = 'Please upload a sheet here to upload all the vehicles info all at once.',
	templateUrl,
	templateText = 'Here is the template:',
	acceptedFormats = '.jpeg,.jpg,.png,.pdf',
	maxSizeMB = 50,
	loading = false,
}) {
	const [fileList, setFileList] = useState([]);

	// Handle file upload
	const handleUpload = (file) => {
		console.log('📁 [BulkUploadModal] File selected:', file);
		console.log('📁 [BulkUploadModal] File details:', {
			name: file?.name,
			size: file?.size,
			type: file?.type,
			lastModified: file?.lastModified,
		});

		if (!file) {
			console.error('❌ [BulkUploadModal] No file object received');
			message.error('Failed to read file. Please try again.');
			return false;
		}

		const isValidFormat = acceptedFormats
			.split(',')
			.some((format) => file.name.toLowerCase().endsWith(format.trim().replace('.', '')));
		const isValidSize = file.size / 1024 / 1024 < maxSizeMB;

		console.log('🔍 [BulkUploadModal] Validation:', {
			fileName: file.name,
			fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
			isValidFormat,
			isValidSize,
			acceptedFormats,
			maxSizeMB,
		});

		if (!isValidFormat) {
			console.error('❌ [BulkUploadModal] Invalid file format');
			message.error(
				`Invalid file format. Please upload ${acceptedFormats
					.split(',')
					.join(', ')
					.toUpperCase()} files only.`
			);
			return false;
		}

		if (!isValidSize) {
			console.error('❌ [BulkUploadModal] File size exceeds limit');
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		console.log('✅ [BulkUploadModal] File validation passed, adding to fileList');
		setFileList([file]);
		message.success(`${file.name} selected successfully`);

		// Trigger file upload to S3 if handler is provided
		if (onFileUpload) {
			console.log('📤 [BulkUploadModal] Calling onFileUpload handler...');
			onFileUpload(file);
		}

		return false; // Prevent auto upload
	};

	// Handle file removal
	const handleRemove = () => {
		console.log('🗑️ [BulkUploadModal] Removing file from list');
		setFileList([]);
		message.info('File removed');
	};

	// Handle submit
	const handleSubmit = () => {
		console.log('📤 [BulkUploadModal] Submit button clicked', {
			fileListLength: fileList.length,
			file: fileList[0],
		});

		if (fileList.length === 0) {
			console.warn('⚠️ [BulkUploadModal] No file selected');
			message.warning('Please select a file to upload');
			return;
		}

		console.log('✅ [BulkUploadModal] Calling onSubmit (file already uploaded to S3)');
		onSubmit();
	};

	// Handle cancel
	const handleCancel = () => {
		console.log('❌ [BulkUploadModal] Modal cancelled, clearing file list');
		setFileList([]);
		onClose();
	};

	// Get file icon based on file type
	const getFileIcon = (fileName) => {
		if (fileName.toLowerCase().endsWith('.pdf')) {
			return <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
		}
		return <FilePdfOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
	};

	return (
		<Modal
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={560}
			centered
			title={null}
			closable={false}
		>
			<ModalContent>
				<ModalTitle>{title}</ModalTitle>
				<ModalDescription>{description}</ModalDescription>

				{/* Upload Area */}
				<UploadArea>
					<UploadText>
						{acceptedFormats
							.split(',')
							.map((format) => format.trim().replace('.', '').toUpperCase())
							.join(', ')}{' '}
						formats up to {maxSizeMB} MB
					</UploadText>
					<Upload
						beforeUpload={handleUpload}
						showUploadList={false}
						accept={acceptedFormats}
						fileList={fileList}
					>
						<UploadButton type="button">
							<CloudUploadOutlined />
							Upload File
						</UploadButton>
					</Upload>
				</UploadArea>

				{/* Template Link */}
				{templateUrl && (
					<Box mb={3}>
						<Text style={{ fontSize: '14px', color: '#666' }}>
							{templateText}{' '}
							<TemplateLink
								href={templateUrl}
								target="_blank"
								rel="noopener noreferrer"
								download={templateUrl.split('/').pop() || undefined}
							>
								URL of the Template
							</TemplateLink>
						</Text>
					</Box>
				)}

				{/* Uploaded File Display */}
				{fileList.length > 0 && (
					<FileItem>
						<FileInfo>
							{getFileIcon(fileList[0].name)}
							<FileName>{fileList[0].name}</FileName>
						</FileInfo>
						<DeleteButton type="button" onClick={handleRemove}>
							×
						</DeleteButton>
					</FileItem>
				)}

				{/* Footer Buttons */}
				<ModalFooter>
					<CancelButton type="button" onClick={handleCancel} disabled={loading}>
						Cancel
					</CancelButton>
					<SubmitButton type="button" onClick={handleSubmit} disabled={loading}>
						{loading ? 'Submitting...' : 'Submit'}
					</SubmitButton>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

BulkUploadModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onFileUpload: PropTypes.func,
	title: PropTypes.string,
	description: PropTypes.string,
	templateUrl: PropTypes.string,
	templateText: PropTypes.string,
	acceptedFormats: PropTypes.string,
	maxSizeMB: PropTypes.number,
	loading: PropTypes.bool,
};

BulkUploadModal.defaultProps = {
	title: 'Bulk Upload',
	description: 'Please upload a sheet here to upload all the vehicles info all at once.',
	templateText: 'Here is the template:',
	acceptedFormats: '.jpeg,.jpg,.png,.pdf',
	maxSizeMB: 50,
	loading: false,
	onFileUpload: null,
};
